import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  FormHelperText,
  Chip
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import { Document, Packer, Paragraph, TextRun, AlignmentType, Header, BorderStyle, Table, TableRow, TableCell, WidthType, ImageRun } from 'docx';
import { saveAs } from 'file-saver';
import axios from 'axios';
import { ReportContext } from '../../Context';

function WrittenStatement() {
  const { report } = useContext(ReportContext);

  const [loading, setLoading] = useState(true);
  const [intervenants, setIntervenants] = useState([]);
  const [personnesPhysiques, setPersonnesPhysiques] = useState([]);
  const [faits, setFaits] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [settings, setSettings] = useState(null);

  const [policier, setPolicier] = useState('');
  const [policierData, setPolicierData] = useState(null);
  const [personne, setPersonne] = useState('');
  const [personneData, setPersonneData] = useState(null);
  const [selectedFaits, setSelectedFaits] = useState([]);
  const [declaration, setDeclaration] = useState('');
  const [signatureDateTime, setSignatureDateTime] = useState('');

  const svgTextToPngBase64 = (svgText, width = 150, height = 150) => {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        const base64 = canvas.toDataURL('image/png').split(',')[1];
        resolve(base64);
      };

      img.onerror = reject;

      const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      img.src = url;
    });
  };

  const base64ToUint8Array = (base64) => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  };

  const loadLogoPng = async () => {
    const svgContent = `<?xml version="1.0" encoding="utf-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 150 150" width="150" height="150">
  <circle cx="75" cy="75" r="74" fill="#FFFFFF" stroke="#244BA6" stroke-width="2"/>
  <path fill="#244BA6" d="M150,75c0-41.4-33.6-75-75-75S0,33.6,0,75c0,39.5,30.5,71.9,69.3,74.8c-3.3-4-7.4-9.5-11-14.6
    c-6.1-8.6-13.4-21.5-13.7-34.4c-0.2-9.6,2-14.8,14.6-34.7c4.9-7.8,8.2-21.1,7.7-23.9c-0.5-2.9,4.6,4.3,6.5,9.1
    c2.5,6.4,2.2,11.2-2.5,24.4c-2.2,6.3-5.9,16.4-2.2,20.9c-0.4-0.5,11.9-13.2,14.8-26.2c2.2-10,1.3-23.3-3-33.1
    c-3.8-8.6-8.3-15.7-7.4-27.1c0.4-4.8,1.7-9.4,1.8-9.7c2.8,7,4,9.5,5.1,11.5c5.1,9.6,15.6,23.7,20.5,33.4
    c5,9.6,8.8,25.5,3,41.7c-5.7,15.9-11.1,21.3-16.9,34.2c-4.3,9.6-7.9,21.4-9.7,28.8C117.5,149,150,115.9,150,75"/>
</svg>`;

    try {
      const base64 = await svgTextToPngBase64(svgContent, 100, 100);
      return base64ToUint8Array(base64);
    } catch (error) {
      console.warn('Erreur conversion logo:', error);
      return null;
    }
  };

  const buildReportNumber = () => {
    if (!reportData || !settings) return 'XXXXXX';

    const shortJudicialDistrict = settings.shortJudicialDistrict || '__';
    const indice = reportData.indice || '__';
    const judicialDivision = settings.judicialDivision || '__';
    const number = report?.number || '__';
    const year = report?.year || '__';

    return `${shortJudicialDistrict}.${indice}.${judicialDivision}.${number}/${year}`;
  };

  const convertGrade = (gradeCode) => {
    const gradeMapping = {
      'AAGP': 'Aspirant Agent de Police',
      'AGP': 'Agent de Police',
      '1AGP': 'Premier Agent de Police',
      'ADAGP': 'Aspirant Agent de sécurisation de Police',
      'DAGP': 'Agent de sécurisation de Police',
      '1DAGP': 'Premier Agent de sécurisation de Police',
      'ABASP': 'Aspirant Assistant de Sécurisation de Police',
      'BASP': 'Assistant de Sécurisation de Police',
      '1BASP': 'Premier Assistant de Sécurisation de Police',
      'BCSP': 'Coordonnateur de Sécurisation de Police',
      '1BCSP': 'Premier Coordonnateur de Sécurisation de Police',
      'AINP': 'Aspirant Inspecteur de Police',
      'INP': 'Inspecteur de Police',
      '1INP': 'Premier Inspecteur de Police',
      'AINPP': 'Aspirant Inspecteur Principal de Police',
      'INPP': 'Inspecteur Principal de Police',
      '1INPP': 'Premier Inspecteur Principal de Police',
      'ACP': 'Aspirant Commissaire de Police',
      'CP': 'Commissaire de Police',
      '1CP': 'Premier Commissaire de Police',
      'CDP': 'Commissaire Divisionnaire de Police',
      '1CDP': 'Premier Commissaire Divisionnaire de Police'
    };
    return gradeMapping[gradeCode] || gradeCode || 'Membre';
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!report || !report.number || !report.year) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const token = sessionStorage.getItem('authToken');

        const settingsResponse = await axios.get('/api/settings');
        setSettings(settingsResponse.data);

        const reportResponse = await axios.get(
          `/api/report/search?number=${report.number}&year=${report.year}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setReportData(reportResponse.data);

        const allUsersResponse = await axios.get(
          '/api/user',
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const reportIntervenants = reportResponse.data.intervenants || [];
        const allUsers = allUsersResponse.data || [];

        const intervenantsOptions = reportIntervenants.map(i => ({
          value: `${convertGrade(i.rank)} ${i.lastname.toUpperCase()} ${i.firstname}`,
          data: {
            ...i,
            rank: convertGrade(i.rank)
          },
          isReportIntervenant: true
        })).sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));

        const allOptions = [...intervenantsOptions];

        if (allUsers.length > 0) {
          allOptions.push({
            value: '--- Autres collègues ---',
            data: null,
            isSeparator: true
          });

          const reportIntervenantIds = reportIntervenants.map(i => i.userId).filter(Boolean);
          const otherUsers = allUsers.filter(user => !reportIntervenantIds.includes(user._id));

          const usersOptions = otherUsers.map(user => {
            const grade = convertGrade(user.rank || '');
            const displayText = `${grade} ${user.lastname.toUpperCase()} ${user.firstname}`;
            return {
              value: displayText,
              data: {
                isExternal: false,
                sign: false,
                rank: grade,
                lastname: user.lastname,
                firstname: user.firstname,
                userId: user._id
              },
              isOtherUser: true
            };
          }).sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));

          allOptions.push(...usersOptions);
        }

        setIntervenants(allOptions);

        const personnesFormatted = (reportResponse.data.personnesPhysiques || [])
          .filter(p => p.lastname && !p.isUnknown)
          .map(p => ({
            value: `${p.lastname.toUpperCase()} ${p.firstname || ''}`,
            data: p
          }));
        setPersonnesPhysiques(personnesFormatted);

        const faitsOptions = (reportResponse.data.faits || []).map(f => ({
          value: f._id || `${Math.random()}`,
          data: f,
          label: formatFaitLabel(f)
        }));
        setFaits(faitsOptions);

        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [report]);

  const handlePolicierChange = (e) => {
    const value = e.target.value;
    setPolicier(value);
    const selected = intervenants.find(i => i.value === value && !i.isSeparator);
    setPolicierData(selected?.data || null);
  };

  const handlePersonneChange = (e) => {
    const value = e.target.value;
    setPersonne(value);
    const selected = personnesPhysiques.find(p => p.value === value);
    setPersonneData(selected?.data || null);
  };

  const handleFaitsChange = (e) => {
    const value = e.target.value;
    setSelectedFaits(typeof value === 'string' ? value.split(',') : value);
  };

  const isFormComplete = policier && personne && selectedFaits.length > 0;

  const formatDateFrench = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const jours = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
    const mois = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

    const jour = jours[date.getDay()];
    const numJour = String(date.getDate()).padStart(2, '0');
    const nomMois = mois[date.getMonth()];
    const annee = date.getFullYear();

    return `${jour} ${numJour} ${nomMois} ${annee}`;
  };

  const formatDateLong = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const mois = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    return `${String(date.getDate()).padStart(2, '0')} ${mois[date.getMonth()]} ${date.getFullYear()}`;
  };

  const formatTimeWithPlural = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const suffix = hours >= 2 ? 'heures' : 'heure';
    return `${String(hours).padStart(2, '0')}:${minutes} ${suffix}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const jour = date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const heures = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const pluriel = date.getHours() >= 2 ? 's' : '';
    return `${jour} à ${heures}:${minutes} heure${pluriel}`;
  };

  const formatFaitDateDescription = (fait) => {
    if (!fait) return '';
    const formatDate = (value) => formatDateTime(value);
    if (fait.timeType === 'range') {
      return `Entre le ${formatDate(fait.rangeLowDateTime)} et le ${formatDate(fait.rangeHighDateTime)}`;
    }
    return `Le ${formatDate(fait.fixedDateTime)}`;
  };

  const formatFaitLabel = (fait) => {
    if (!fait) return '';
    if (fait.isUndetermined) {
      return 'Fait non déterminé - procès-verbal subséquent';
    }
    const introductions = [];
    if (fait.isQualified) introductions.push('Fait qualifié de');
    if (fait.isTentative) introductions.push('Tentative de');
    const noyau = fait.fait || '';
    let label = introductions.length
      ? `${introductions.map((introduction, index) => {
          const suivants = introductions.length - index;
          const libelle = index === 0
            ? introduction
            : `${introduction.charAt(0).toLowerCase()}${introduction.slice(1)}`;
          return suivants > 1 ? libelle : `${libelle} :`;
        }).join(' ')} ${noyau}`.trim()
      : noyau;
    const dateDesc = formatFaitDateDescription(fait);
    if (dateDesc) label += ` — ${dateDesc}`;
    if (fait.address) label += ` — À ${fait.address}`;
    return label.trim();
  };

  const infoParagraph = (label, value, options = {}) => {
    if (!value) return null;
    const { bold = true, underline = false } = options;
    return new Paragraph({
      indent: { left: 720 }, // alinéa
      children: [
        new TextRun({ text: label, font: 'Helvetica', size: 20 }),
        new TextRun({
          text: value,
          font: 'Helvetica',
          size: 20,
          bold,
          underline: underline ? {} : undefined
        })
      ]
    });
  };

  const buildHeader = (logoImageData, reportNumber, typeValue) => {
    const noBorders = {
      top: { style: BorderStyle.NONE },
      bottom: { style: BorderStyle.NONE },
      left: { style: BorderStyle.NONE },
      right: { style: BorderStyle.NONE },
    };

    const headerRows = [
      new TableRow({
        children: [
          new TableCell({
            width: { size: 12, type: WidthType.PERCENTAGE },
            borders: noBorders,
            children: [
              new Paragraph({
                children: logoImageData ? [
                  new ImageRun({
                    data: logoImageData,
                    transformation: { width: 40, height: 40 },
                    type: 'png',
                  })
                ] : []
              })
            ]
          }),
          new TableCell({
            width: { size: 38, type: WidthType.PERCENTAGE },
            borders: noBorders,
            children: [
              new Paragraph({
                spacing: { after: 40 },
                children: [
                  new TextRun({
                    text: `Police ${typeValue}`,
                    font: 'Helvetica',
                    size: 18,
                  }),
                ],
              }),
              new Paragraph({
                spacing: { after: 40 },
                children: [
                  new TextRun({
                    text: `${settings?.clientName || 'N/A'} - ${settings?.zoneNumber || 'N/A'}`,
                    font: 'Helvetica',
                    size: 18,
                    bold: true,
                  }),
                ],
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Service ${reportData?.unit || ''}`,
                    font: 'Helvetica',
                    size: 18,
                  }),
                ],
              }),
            ],
          }),
          new TableCell({
            width: { size: 50, type: WidthType.PERCENTAGE },
            borders: noBorders,
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                spacing: { after: 40 },
                children: [
                  new TextRun({
                    text: `Procès-verbal ${reportData?.reportType === "subsequent" ? "subséquent" : reportData?.reportType}`,
                    font: 'Helvetica',
                    size: 18,
                  }),
                ],
              }),
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                spacing: { after: 40 },
                children: [
                  new TextRun({
                    text: reportNumber,
                    font: 'Helvetica',
                    size: 18,
                    bold: true,
                  }),
                ],
              }),
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({
                    text: 'Annexe n° _____',
                    font: 'Helvetica',
                    size: 18
                  }),
                ],
              }),
            ],
          }),
        ],
      }),
    ];

    const headerChildren = [
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: BorderStyle.NONE },
          bottom: { style: BorderStyle.NONE },
          left: { style: BorderStyle.NONE },
          right: { style: BorderStyle.NONE },
          insideHorizontal: { style: BorderStyle.NONE },
          insideVertical: { style: BorderStyle.NONE },
        },
        rows: headerRows,
      }),
      new Paragraph({
        border: {
          bottom: {
            color: '000000',
            space: 1,
            style: BorderStyle.SINGLE,
            size: 6,
          },
        },
        spacing: { after: 200, before: 0, line: 20 },
        children: [
          new TextRun({
            text: '',
            size: 4,
          }),
        ],
      })
    ];

    return new Header({ children: headerChildren });
  };

  const buildIdentityParagraphs = () => {
    if (!personneData) return [];

    const sexeFr = personneData.gender === 'M' ? 'Masculin' :
      personneData.gender === 'F' ? 'Féminin' :
        personneData.gender || '';

    const dateNaissance = personneData.birthdate ?
      `${String(new Date(personneData.birthdate).getDate()).padStart(2, '0')}/${String(new Date(personneData.birthdate).getMonth() + 1).padStart(2, '0')}/${new Date(personneData.birthdate).getFullYear()}` : '';

    const birthLine = dateNaissance ? `${dateNaissance}${personneData.birthPlace ? ` à ${personneData.birthPlace}` : ''}` : '';

    const buildNationalityParagraph = () => {
      const nat = personneData.nationality;
      const rrn = personneData.nationalNumber;

      if (!nat && !rrn) return null;

      if (nat && !rrn) {
        return infoParagraph('Nationalité : ', nat, { bold: false });
      }

      if (nat && rrn) {
        return new Paragraph({
          children: [
            new TextRun({ text: 'Nationalité : ', font: 'Helvetica', size: 20 }),
            new TextRun({ text: nat, font: 'Helvetica', size: 20 }),
            new TextRun({ text: ' (RRN : ', font: 'Helvetica', size: 20 }),
            new TextRun({ text: rrn, font: 'Helvetica', size: 20, bold: true }),
            new TextRun({ text: ')', font: 'Helvetica', size: 20 }),
          ]
        });
      }

      return new Paragraph({
        children: [
          new TextRun({ text: 'RRN : ', font: 'Helvetica', size: 20 }),
          new TextRun({ text: rrn, font: 'Helvetica', size: 20, bold: true }),
        ]
      });
    };

    return [
      new Paragraph({
        indent: { left: 720 }, // alinéa
        children: [
          new TextRun({ text: 'Nom et prénom : ', font: 'Helvetica', size: 20 }),
          new TextRun({
            text: (personneData.lastname || '').toUpperCase(),
            font: 'Helvetica',
            size: 20,
            bold: true,
            underline: {}
          }),
          new TextRun({ text: ' ', font: 'Helvetica', size: 20 }),
          new TextRun({
            text: personneData.firstname || '',
            font: 'Helvetica',
            size: 20,
            bold: true
          }),
          personneData.otherFirstnames ? new TextRun({
            text: ` ${personneData.otherFirstnames}`,
            font: 'Helvetica',
            size: 20
          }) : null
        ].filter(Boolean)
      }),
      infoParagraph('Date et lieu de naissance : ', birthLine),
      infoParagraph('Sexe : ', sexeFr),
      infoParagraph('État civil : ', personneData.civilStatus || ''),
      buildNationalityParagraph(),
      infoParagraph('Profession : ', personneData.profession || ''),
      infoParagraph('Domicile : ', personneData.residence || ''),
    ].filter(Boolean);
  };

  const buildFactsParagraphs = () => {
    const selected = faits.filter(f => selectedFaits.includes(f.value));
    if (selected.length === 0) return [];

    return selected.map(f => new Paragraph({
      bullet: { level: 1 },
      children: [
        new TextRun({
          text: formatFaitLabel(f.data),
          font: 'Helvetica',
          size: 20
        })
      ]
    }));
  };

  const buildPlaceholderDeclaration = () => {
    const lines = [];
    // Retour à la ligne avant le premier trait
    lines.push(new Paragraph({ text: '' }));

    for (let i = 0; i < 30; i++) {
      // Ligne horizontale
      lines.push(new Paragraph({
        border: {
          bottom: {
            color: '000000',
            space: 1,
            style: BorderStyle.SINGLE,
            size: 6,
          },
        },
        children: [
          new TextRun({
            text: '',
            font: 'Helvetica',
            size: 20
          })
        ]
      }));
      // Espace entre les lignes
      lines.push(new Paragraph({ text: '' }));
    }
    return lines;
  };

  const handleGenerate = async () => {
    if (!isFormComplete || !reportData || !settings || !policierData || !personneData) {
      console.error('Données manquantes pour générer le document');
      return;
    }

    try {
      const reportNumber = buildReportNumber();
      const dateLongue = signatureDateTime ? formatDateFrench(signatureDateTime) : '';
      const logoImageData = await loadLogoPng();
      const typeValue = settings.type === 'local' ? 'Locale' : settings.type === 'federal' ? 'Fédérale' : settings.type || 'N/A';

      const doc = new Document({
        sections: [{
          properties: {},
          headers: {
            default: buildHeader(logoImageData, reportNumber, typeValue)
          },
          children: [
            new Table({
              width: {
                size: 100,
                type: WidthType.PERCENTAGE
              },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                left: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                right: { style: BorderStyle.SINGLE, size: 1, color: '000000' }
              },
              rows: [
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          alignment: AlignmentType.CENTER,
                          children: [
                            new TextRun({
                              text: 'FORMULAIRE DE DECLARATION ECRITE',
                              font: 'Helvetica',
                              size: 28,
                              bold: true
                            })
                          ]
                        })
                      ]
                    })
                  ]
                })
              ]
            }),

            new Paragraph({ text: '' }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `Nous, ${policierData.rank} `,
                  font: 'Helvetica',
                  size: 20
                }),
                new TextRun({
                  text: policierData.lastname?.toUpperCase() || '',
                  font: 'Helvetica',
                  size: 20,
                  bold: true
                }),
                new TextRun({ text: ' ', font: 'Helvetica', size: 20 }),
                new TextRun({
                  text: policierData.firstname || '',
                  font: 'Helvetica',
                  size: 20,
                  bold: true
                }),
                new TextRun({ text: ',', font: 'Helvetica', size: 20 }),
              ]
            }),

            new Paragraph({
              bullet: { level: 0 },
              children: [
                new TextRun({
                  text: 'remettons le présent formulaire de déclaration à :',
                  font: 'Helvetica',
                  size: 20
                })
              ]
            }),

            ...buildIdentityParagraphs(),

            new Paragraph({
              bullet: { level: 0 },
              children: [
                new TextRun({
                  text: `informons l'intéressé${personneData.gender === 'F' ? 'e' : ''} qu’il apparaît des éléments de l’information que des faits qualifiés infraction peuvent lui être reproché, en l’espèce :`,
                  font: 'Helvetica',
                  size: 20
                })
              ]
            }),

            ...buildFactsParagraphs(),

            new Paragraph({
              bullet: { level: 0 },
              children: [
                new TextRun({
                  text: 'l’informons également :',
                  font: 'Helvetica',
                  size: 20
                })
              ]
            }),

            ...(() => {
              const isFemale = personneData?.gender === 'F';
              const rightsTexts = [
                isFemale
                  ? '1° qu\'elle a le droit de ne pas s’incriminer elle-même ;'
                  : '1° qu\'il a le droit de ne pas s’incriminer lui-même ;',
                isFemale
                  ? '2° qu\'elle a le droit de ne pas coopérer à l\'enquête et de ne pas témoigner à sa propre charge ;'
                  : '2° qu\'il a le droit de ne pas coopérer à l\'enquête et de ne pas témoigner à sa propre charge ;',
                isFemale
                  ? '3° qu\'elle a le droit de se taire et peut donc refuser de noter sa déclaration par écrit ;'
                  : '3° qu\'il a le droit de se taire et peut donc refuser de noter sa déclaration par écrit ;',
                isFemale
                  ? '4° que ses déclarations écrites peuvent être utilisées comme preuve en justice et peuvent de ce fait être utilisées contre elle ou des tiers ou peuvent donner lieu à une poursuite d\'enquête ;'
                  : '4° que ses déclarations écrites peuvent être utilisées comme preuve en justice et peuvent de ce fait être utilisées contre lui ou des tiers ou peuvent donner lieu à une poursuite d\'enquête ;',
                isFemale
                  ? '5° qu\'elle peut demander à être entendue si elle conteste les faits ou souhaite apporter un autre éclairage sur ces faits.'
                  : '5° qu\'il peut demander à être entendu s\'il conteste les faits ou souhaite apporter un autre éclairage sur ces faits.'
              ];
              return rightsTexts.map(text => new Paragraph({
              bullet: { level: 1 },
              children: [
                new TextRun({
                  text,
                  font: 'Helvetica',
                  size: 20
                })
              ]
              }));
            })(),

            new Paragraph({ text: '' }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `Déclaration écrite de ${(personneData.lastname || '').toUpperCase()} ${personneData.firstname || ''} :`,
                  font: 'Helvetica',
                  size: 20,
                  bold: true,
                  underline: {}
                })
              ]
            }),

            ...(declaration?.trim()
              ? [new Paragraph({
                children: [
                  new TextRun({
                    text: declaration.trim(),
                    font: 'Helvetica',
                    size: 20
                  })
                ]
              })]
              : buildPlaceholderDeclaration()),

            new Paragraph({ text: '' }),

            new Paragraph({
              alignment: AlignmentType.RIGHT,
              children: [
                new TextRun({
                  text: signatureDateTime
                    ? `Fait le ${formatDateLong(signatureDateTime)} à ${formatTimeWithPlural(signatureDateTime)}`
                    : 'Fait le _____/_____/_____ à _____:_____ heure(s)',
                  font: 'Helvetica',
                  size: 20
                })
              ]
            }),

            new Paragraph({
              alignment: AlignmentType.RIGHT,
              children: [
                new TextRun({
                  text: 'Signature de ',
                  font: 'Helvetica',
                  size: 20
                }),
                new TextRun({
                  text: (personneData.lastname || '').toUpperCase(),
                  font: 'Helvetica',
                  size: 20,
                  bold: true,
                  underline: {}
                }),
                new TextRun({
                  text: ` ${personneData.firstname || ''}`,
                  font: 'Helvetica',
                  size: 20,
                  bold: true
                })
              ]
            })
          ]
        }]
      });

      const blob = await Packer.toBlob(doc);
      const fileName = `${reportNumber}-declaration-ecrite.docx`;
      saveAs(blob, fileName);

      // Log du téléchargement
      try {
        const token = sessionStorage.getItem('authToken');
        const faitsSelectionnesLabels = faits
          .filter(f => selectedFaits.includes(f.value))
          .map(f => f.label);
        
        await axios.post('/api/appendices/log-download', {
          appendixType: 'WrittenStatement',
          appendixTitle: 'Déclaration écrite',
          reportNumber: `${report?.number}/${report?.year}`,
          formData: {
            policierQuiInforme: `${policierData.rank} ${policierData.lastname?.toUpperCase()} ${policierData.firstname}`,
            personneConcernee: `${personneData.lastname?.toUpperCase()} ${personneData.firstname}`,
            faits: faitsSelectionnesLabels,
            declarationRemplie: declaration?.trim() ? true : false,
            dateHeureSignature: signatureDateTime || 'Non spécifiée'
          }
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (logError) {
        console.warn('Erreur lors du log du téléchargement:', logError);
        // Ne pas bloquer le téléchargement en cas d'erreur de log
      }
    } catch (error) {
      console.error('Erreur lors de la génération du document:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Complétez les informations ci-dessous pour générer la déclaration écrite.
        </Typography>

        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth required>
            <InputLabel id="policier-declaration-label">Policier qui informe</InputLabel>
            <Select
              labelId="policier-declaration-label"
              value={policier}
              onChange={handlePolicierChange}
              label="Policier qui informe"
            >
              {intervenants.map((intervenant, index) => {
                const isSeparator = intervenant.isSeparator;
                return (
                  <MenuItem
                    key={index}
                    value={intervenant.value}
                    disabled={isSeparator}
                    sx={isSeparator ? {
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      cursor: 'default'
                    } : {}}
                  >
                    {intervenant.value}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <FormControl fullWidth required>
            <InputLabel id="personne-concernee-label">Personne concernée</InputLabel>
            <Select
              labelId="personne-concernee-label"
              value={personne}
              onChange={handlePersonneChange}
              label="Personne concernée"
            >
              {personnesPhysiques.map((p, index) => (
                <MenuItem key={index} value={p.value}>
                  {p.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required>
            <InputLabel id="faits-label">Faits</InputLabel>
            <Select
              labelId="faits-label"
              multiple
              value={selectedFaits}
              onChange={handleFaitsChange}
              label="Faits"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const fact = faits.find(f => f.value === value);
                    return <Chip key={value} label={fact?.label || value} />;
                  })}
                </Box>
              )}
            >
              {faits.map((f) => (
                <MenuItem key={f.value} value={f.value}>
                  {f.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Déclaration écrite"
            multiline
            minRows={4}
            value={declaration}
            onChange={(e) => setDeclaration(e.target.value)}
            helperText="Laissez ce champ vide si la déclaration sera rédigée ultérieurement"
          />

          <TextField
            fullWidth
            label="Date et heure de signature"
            type="datetime-local"
            value={signatureDateTime}
            onChange={(e) => setSignatureDateTime(e.target.value)}
            InputLabelProps={{
              shrink: true
            }}
            helperText="Laissez ce champ vide si la déclaration sera rédigée ultérieurement"
          />

          {isFormComplete && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<DescriptionIcon />}
                onClick={handleGenerate}
                sx={{
                  minWidth: 250,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
              >
                Générer le document
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default WrittenStatement;

